import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Space, Input, InputNumber, Form, Select, Row, Col, Tag, Typography, notification,
  DatePicker, Popconfirm, AutoComplete, Card, Breadcrumb, List, Spin
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, DeleteOutlined, TeamOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import UploadButton from '../../../components/UploadButton';
import {
  getPremiumOrderInfoRequest,
  getPremiumOrderOptionsRequest,
  createPremiumOrderRequest,
  updatePremiumOrderRequest,
  deletePremiumOrderRequest,
  searchUsersRequest
} from '../../../services/cms/premiumOrderServiceRequest';
import { listAllActiveServicesRequest } from '../../../services/cms/serviceServiceRequest';

const { Text, Title } = Typography;
const { TextArea } = Input;

// Status color mapping
const STATUS_COLORS = {
  1: 'blue',
  2: 'orange',
  3: 'red',
  4: 'cyan',
  5: 'green',
  6: 'purple',
  7: 'default',
  8: 'geekblue',
  9: 'lime',
  10: 'gold',
  11: 'volcano',
  12: 'magenta',
  13: 'processing',
};

const DetailDonHang = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formOrder] = Form.useForm();
  
  const isCreate = id === 'new';

  const dataRedux = useSelector((state) => state.user);
  const userRole = dataRedux?.info?.role;
  const isAdmin = userRole === 9999;
  const userPermissions = (dataRedux?.info?.permissions || []).map((item) => item.key_menu);
  const hasAssignPermission = isAdmin || userPermissions.includes('/quan-ly-don-hang');

  // Watch fields
  const formStatusRaw = Form.useWatch('status', formOrder);
  const formStatus = formStatusRaw !== undefined && formStatusRaw !== null ? Number(formStatusRaw) : undefined;
  const formFeaturesId = Form.useWatch('features_id', formOrder);

  const [prevStatus, setPrevStatus] = useState();

  // Auto-fill logic based on selected status & payment
  useEffect(() => {
    if (formStatus !== undefined) {
      if (prevStatus !== undefined && formStatus !== prevStatus) {
        formOrder.setFieldValue('reason', undefined);
      } else if (formStatus !== 2 && formStatus !== 4) {
        formOrder.setFieldValue('reason', undefined);
      }
      setPrevStatus(formStatus);
    }
  }, [formStatus, prevStatus, formOrder]);

  // Queries
  const { data: orderData, isLoading: isLoadingOrder, refetch: refetchOrder } = useQuery({
    queryKey: ['premiumOrderDetail', id],
    queryFn: () => getPremiumOrderInfoRequest({ id }),
    enabled: !isCreate,
  });

  const { data: options, isLoading: isLoadingOptions } = useQuery({
    queryKey: ['premiumOrderOptionsDetail'],
    queryFn: () => getPremiumOrderOptionsRequest({}),
  });

  const { data: allActiveServices } = useQuery({
    queryKey: ['allActiveServicesDetail'],
    queryFn: () => listAllActiveServicesRequest({}),
  });

  // Options formatting
  const activeServiceOptions = useMemo(() => {
    if (!allActiveServices) return [];
    return allActiveServices.map(s => ({ value: s.id, label: `${s.id} - ${s.name}` }));
  }, [allActiveServices]);

  const filteredPremiumTypes = useMemo(() => {
    const allTypes = options?.premiumTypes || [];
    if (formFeaturesId !== undefined && formFeaturesId !== null && formFeaturesId !== '') {
      return allTypes.filter(pt => String(pt.service_id) === String(formFeaturesId));
    }
    return allTypes;
  }, [options?.premiumTypes, formFeaturesId]);

  const statusOptions = useMemo(() => {
    if (!options?.statuses) return [];
    return Object.entries(options.statuses).map(([k, v]) => ({ value: Number(k), label: v }));
  }, [options?.statuses]);

  const reasonOptions = useMemo(() => {
    if (!options?.reasons) return [];
    if (Array.isArray(options.reasons)) return options.reasons.map((r) => ({ value: r, label: r }));
    return Object.entries(options.reasons).map(([k, v]) => ({ value: v, label: v }));
  }, [options?.reasons]);

  const reasonsByStatusOptions = useMemo(() => {
    if (!options?.reasonsByStatus || !formStatus) return [];
    const list = options.reasonsByStatus[formStatus] || [];
    return list.map((r) => ({ value: r, label: r }));
  }, [options?.reasonsByStatus, formStatus]);

  // Debounced search for user_id auto-complete
  const [modalUserSearchResults, setModalUserSearchResults] = useState([]);
  const [modalUserSearchLoading, setModalUserSearchLoading] = useState(false);

  const debouncedSearchUsers = useCallback((keyword) => {
    setModalUserSearchLoading(true);
    const timeoutId = setTimeout(async () => {
      if (keyword && keyword.trim() !== '') {
        try {
          const results = await searchUsersRequest({ keyword: keyword.trim() });
          if (Array.isArray(results)) setModalUserSearchResults(results);
        } catch (error) {
          console.error('Lỗi khi search users:', error);
        }
      } else {
        setModalUserSearchResults([]);
      }
      setModalUserSearchLoading(false);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleUserSelect = useCallback((value) => {
    const found = modalUserSearchResults.find((u) => u.user_id === value);
    if (found) {
      formOrder.setFieldsValue({
        phone: found.phone || formOrder.getFieldValue('phone'),
        email: found.email || formOrder.getFieldValue('email'),
        fullname: found.fullname || formOrder.getFieldValue('fullname'),
      });
    }
  }, [modalUserSearchResults, formOrder]);

  // Fill form values when orderData loads
  useEffect(() => {
    if (orderData) {
      formOrder.setFieldsValue({
        user_id: orderData.user_id,
        phone: orderData.phone,
        email: orderData.email,
        fullname: orderData.fullname,
        status: orderData.status,
        reason: orderData.reason,
        premium_type_id: orderData.premium_type_id,
        features_id: orderData.features_id,
        platform: orderData.platform,
        payment_channel: orderData.payment_channel,
        campaign_id: orderData.campaign_id,
        order_type: orderData.order_type,
        work_id: orderData.work_id,
        app_version: orderData.app_version,
        pay_amount: orderData.pay_amount,
        price_purchased: orderData.price_purchased,
        payment_time: orderData.payment_time ? dayjs(orderData.payment_time) : null,
        transaction_id: orderData.transaction_id,
        image_transaction_result: orderData.image_transaction_result,
        call_numb: (orderData.order_calls?.length || 0) + 1,
        note: '', // reset note field for a new call note
        create_time_formatted: orderData.create_time_formatted,
        staff_id: orderData.staff_id || null,
      });
    } else if (isCreate) {
      formOrder.resetFields();
      formOrder.setFieldsValue({
        status: 1,
        call_numb: 1,
        platform: 'cms',
        order_type: 2,
        create_time: dayjs(),
      });
    }
  }, [orderData, isCreate, formOrder]);

  // Mutations
  const { mutate: mutateCreate, isPending: isCreating } = useMutation({
    mutationFn: createPremiumOrderRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Tạo đơn hàng thành công!' });
        navigate('/cms/quan-ly-don-hang');
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
  });

  const { mutate: mutateUpdate, isPending: isUpdating } = useMutation({
    mutationFn: updatePremiumOrderRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Cập nhật đơn hàng thành công!' });
        navigate('/cms/quan-ly-don-hang');
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deletePremiumOrderRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Xóa đơn hàng thành công!' });
        navigate('/cms/quan-ly-don-hang');
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
      }
    },
  });

  // Check editable permission based on paid status
  const isPaidStatus = (s) => [5, 12, 13].includes(Number(s));
  const isPaidEditing = !isCreate && orderData && isPaidStatus(orderData?.status);

  const handleSubmit = () => {
    formOrder.validateFields().then(values => {
      const payload = { ...values };

      if (payload.app_version !== undefined) {
        payload.app_id = payload.app_version;
      }

      const stringFields = ['features_id', 'call_numb', 'payment_channel', 'platform', 'app_version', 'app_id'];
      stringFields.forEach(field => {
        if (payload[field] !== undefined && payload[field] !== null) {
          payload[field] = String(payload[field]);
        }
      });

      if (payload.reason !== undefined && payload.reason !== null) {
        payload.reason = String(payload.reason);
      }

      // Giờ thanh toán phải kèm múi giờ (vd 2026-09-15T10:32:00+07:00). Chuỗi trần 'YYYY-MM-DD HH:mm:ss'
      // bị API (pod UTC) hiểu là giờ UTC → mỗi lần bấm Lưu ghi DB lệch +7 tiếng (DON_TON_DONG.md mục 32).
      const paymentTimeGoc = orderData?.payment_time ? dayjs(orderData.payment_time) : null;
      const paymentTimeMoi = payload.payment_time ? dayjs(payload.payment_time) : null;
      const paymentTimeKhongDoi = !isCreate && (
        isPaidEditing ||
        (!paymentTimeGoc && !paymentTimeMoi) ||
        (!!paymentTimeGoc && !!paymentTimeMoi && paymentTimeMoi.isSame(paymentTimeGoc))
      );
      if (payload.payment_time && typeof payload.payment_time.format === 'function') {
        payload.payment_time = payload.payment_time.format();
      }

      if (!isCreate) {
        const updatePayload = {
          id: Number(id),
          call_note: payload.note,
          call_reason: payload.reason !== undefined && payload.reason !== null ? String(payload.reason) : null,
        };

        const updateKeys = [
          'user_id', 'fullname', 'email', 'phone', 'app_id', 'status', 'staff_id', 'features_id', 
          'campaign_id', 'premium_type_id', 'work_id', 'service_type', 'content', 'lv_camp', 
          'payment_channel', 'platform', 'order_type', 'image_transaction_result', 'pay_amount', 
          'voucher_id', 'reason', 'price_purchased', 'payment_time', 'call_numb', 'transaction_id', 'customer_id'
        ];

        updateKeys.forEach(k => {
          if (payload[k] !== undefined) updatePayload[k] = payload[k];
        });
        // Ô giờ thanh toán đang khoá (đơn đã thanh toán) hoặc không đổi → không gửi,
        // để API không ghi đè giờ giao dịch của history.
        if (paymentTimeKhongDoi) delete updatePayload.payment_time;
        mutateUpdate(updatePayload);
      } else {
        const createPayload = {};
        const createKeys = [
          'user_id', 'fullname', 'email', 'phone', 'app_id', 'status', 'staff_id', 'features_id', 
          'campaign_id', 'premium_type_id', 'work_id', 'service_type', 'content', 'lv_camp', 
          'payment_channel', 'platform', 'order_type', 'image_transaction_result', 'pay_amount', 
          'voucher_id', 'reason', 'price_purchased', 'payment_time', 'customer_id', 'note', 'call_numb', 'transaction_id'
        ];
        createKeys.forEach(k => {
          if (payload[k] !== undefined) createPayload[k] = payload[k];
        });
        mutateCreate(createPayload);
      }
    }).catch((err) => {
      console.error('Validation fails:', err);
    });
  };

  const isPageLoading = (!isCreate && isLoadingOrder) || isLoadingOptions;

  if (isPageLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Đang tải dữ liệu đơn hàng..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1440px', margin: '0 auto' }}>
      {/* Breadcrumb & Navigation */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/info">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item href="/cms/quan-ly-don-hang">Quản lý đơn hàng</Breadcrumb.Item>
        <Breadcrumb.Item>{isCreate ? 'Tạo đơn hàng mới' : `Đơn hàng #${id}`}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cms/quan-ly-don-hang')} />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isCreate ? 'Tạo đơn hàng mới' : `Chi tiết đơn hàng #${id}`}
            </Title>
            {!isCreate && orderData && (
              <div style={{ marginTop: 4 }}>
                <span style={{ marginRight: 16, color: '#666' }}>Ngày gửi: {orderData.create_time_formatted || '--'}</span>
                <Tag color={STATUS_COLORS[orderData.status] || 'default'}>
                  {orderData.status_name || 'Không rõ'}
                </Tag>
              </div>
            )}
          </div>
        </div>

        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cms/quan-ly-don-hang')}>
            Quay lại
          </Button>
          {!isCreate && (
            <Popconfirm
              title="Xóa đơn hàng này?"
              onConfirm={() => mutateDelete({ id: Number(id) })}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button danger icon={<DeleteOutlined />}>
                Xóa đơn hàng
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isCreating || isUpdating}
            onClick={handleSubmit}
          >
            Lưu lại
          </Button>
        </Space>
      </div>

      <Form form={formOrder} layout="vertical" validateTrigger={['onBlur', 'onChange']}>
        <Row gutter={24}>
          {/* Main Info Column */}
          <Col xs={24} lg={15}>
            {/* Customer Info Card */}
            <Card title="1. Thông tin khách hàng" style={{ marginBottom: 20, borderRadius: 8 }} shadow="sm">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="user_id" label="Khách hàng (ID/SĐT/Email)">
                    <Select
                      disabled={isPaidEditing}
                      showSearch
                      allowClear
                      placeholder="Nhập để tìm..."
                      filterOption={false}
                      onSearch={debouncedSearchUsers}
                      onChange={handleUserSelect}
                      onClear={() => setModalUserSearchResults([])}
                      loading={modalUserSearchLoading}
                      notFoundContent={modalUserSearchLoading ? 'Đang tải...' : 'Không có dữ liệu'}
                      options={modalUserSearchResults.map((u) => ({
                        value: u.user_id,
                        label: `${u.user_id} - ${u.phone || ''} - ${u.email || ''}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="phone" label="Số điện thoại">
                    <Input placeholder="Số điện thoại" disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email không hợp lệ!' }]}>
                    <Input placeholder="Email" disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="fullname" label="Tên khách hàng">
                    <Input placeholder="Nhập tên khách hàng" disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Service & Details Info Card */}
            <Card title="2. Thông tin dịch vụ & Phân loại" style={{ marginBottom: 20, borderRadius: 8 }} shadow="sm">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="features_id" label="Tính năng (Dịch vụ)">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn dịch vụ"
                      disabled={isPaidEditing}
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={activeServiceOptions}
                      onChange={() => {
                        formOrder.setFieldValue('premium_type_id', undefined);
                        formOrder.setFieldValue('pay_amount', undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="premium_type_id" label="Gói mua">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn gói mua"
                      disabled={isPaidEditing}
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={filteredPremiumTypes.map(pt => ({
                        value: pt.id,
                        label: `${pt.id}-${pt.description || ''}-${pt.name}`,
                        price: pt.price,
                      }))}
                      onChange={(val, option) => {
                        if (option?.price !== undefined) {
                          formOrder.setFieldValue('pay_amount', option.price);
                        }
                        if (val && options?.premiumTypes) {
                          const pt = options.premiumTypes.find(p => p.id === val);
                          if (pt && pt.service_id) {
                            formOrder.setFieldValue('features_id', pt.service_id);
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="platform" label="Nguồn ghi nhận đơn (Platform)">
                    <Select
                      allowClear
                      placeholder="Chọn platform"
                      disabled={isPaidEditing}
                      options={options?.platforms || []}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="app_version" label="Phiên bản ứng dụng">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn app version"
                      options={options?.appVersions?.map(v => ({ value: v, label: v })) || []}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="campaign_id" label="Chiến dịch">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn chiến dịch"
                      disabled={isPaidEditing}
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={options?.campaigns?.map(c => ({ value: c.id, label: c.title })) || []}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="work_id" label="Việc chọn (Zodiac/ZodiacWork)">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn công việc"
                      disabled={isPaidEditing}
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={options?.workList || []}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {hasAssignPermission && (
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="staff_id" label="Nhân viên chăm sóc">
                      <Select
                        showSearch
                        allowClear
                        placeholder="Chọn nhân viên chăm sóc"
                        disabled={!isCreate && orderData && isPaidStatus(orderData?.status)}
                        filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        options={options?.staff?.map(s => ({ value: s.id, label: s.username })) || []}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>

            {/* Payment Card */}
            <Card title="3. Thông tin thanh toán" style={{ marginBottom: 20, borderRadius: 8 }} shadow="sm">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="pay_amount" label="Số tiền cần thanh toán (đ)">
                    <InputNumber
                      placeholder="Số tiền cần thanh toán"
                      style={{ width: '100%' }}
                      disabled={isPaidEditing}
                      formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                      parser={v => v?.replace(/,/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="price_purchased" label="Số tiền đã thanh toán (đ)">
                    <InputNumber
                      placeholder="Số tiền đã thanh toán"
                      style={{ width: '100%' }}
                      disabled={isPaidEditing}
                      formatter={v => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                      parser={v => v?.replace(/,/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {Number(formStatus) !== 11 && (
              <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="payment_channel" label="Kênh thanh toán">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Chọn kênh thanh toán"
                      disabled={isPaidEditing}
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                      options={options?.paymentChannels || []}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="payment_time" label="Thời gian thanh toán">
                    <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="transaction_id" label="Mã giao dịch (Transaction ID)">
                    <Input placeholder="Mã giao dịch" disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="image_transaction_result" label="Ảnh chụp giao dịch">
                    <UploadButton buttonText="Tải ảnh giao dịch" maxCount={1} disabled={isPaidEditing} />
                  </Form.Item>
                </Col>
              </Row>
              </>
              )}
            </Card>

            {/* Trạng thái đơn hàng Card */}
            <Card title="4. Trạng thái xử lý" style={{ marginBottom: 20, borderRadius: 8 }} shadow="sm">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="status" label="Trạng thái" rules={[{ required: true, message: 'Vui lòng chọn trạng thái!' }]}>
                    <Select allowClear placeholder="Chọn trạng thái" options={statusOptions} disabled={!isCreate && orderData && isPaidStatus(orderData?.status)} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {(Number(formStatus) === 2 || Number(formStatus) === 4) && (
                    <Form.Item name="reason" label="Lý do" rules={[{ required: true, message: 'Vui lòng chọn lý do!' }]}>
                      <AutoComplete
                        allowClear
                        placeholder="Chọn hoặc nhập lý do"
                        options={reasonsByStatusOptions}
                        filterOption={(inputValue, option) => {
                          const val = formOrder.getFieldValue('reason');
                          if (val && inputValue === val) return true;
                          return option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1;
                        }}
                      />
                    </Form.Item>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Contact History & Notes Column */}
          <Col xs={24} lg={9}>
            {/* Create Call Note Card */}
            <Card title="Cập nhật liên hệ mới" style={{ marginBottom: 20, borderRadius: 8 }} headStyle={{ background: '#fafafa' }} shadow="sm">
              <Form.Item name="call_numb" label="Lần liên hệ thứ">
                <InputNumber style={{ width: '100%' }} min={1} placeholder="Lần liên hệ" />
              </Form.Item>
              <Form.Item name="note" label="Nội dung ghi chú liên hệ">
                <TextArea rows={4} placeholder="Nhập nội dung trao đổi với khách hàng..." />
              </Form.Item>
              <Button type="primary" block icon={<SaveOutlined />} onClick={handleSubmit} loading={isCreating || isUpdating}>
                Cập nhật và Lưu đơn hàng
              </Button>
            </Card>

            {/* Contact History List Card */}
            {!isCreate && orderData && (
              <Card title={`Lịch sử chăm sóc (${orderData.order_calls?.length || 0} lần)`} style={{ borderRadius: 8 }} headStyle={{ background: '#fafafa' }} shadow="sm">
                {orderData.order_calls?.length > 0 ? (
                  <List
                    itemLayout="vertical"
                    dataSource={orderData.order_calls}
                    renderItem={(call, idx) => (
                      <List.Item style={{ padding: '12px 0', borderBottom: idx === orderData.order_calls.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong>Lần {call.call_numb || idx + 1}</Text>
                          <Tag color={STATUS_COLORS[call.status] || 'default'}>
                            {options?.statuses?.[call.status] || 'Không rõ'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 8 }}>
                          {call.modify_time ? dayjs(call.modify_time).format('DD/MM/YYYY HH:mm') : ''} — <TeamOutlined /> {call.modify_name || 'Operator'}
                        </div>
                        <div style={{ background: '#f9f9f9', padding: '8px 12px', borderRadius: 4 }}>
                          {call.note ? (
                            <div><Text style={{ fontSize: '13px' }}>{call.note}</Text></div>
                          ) : (
                            <Text type="secondary" italic style={{ fontSize: '13px' }}>Không có nội dung</Text>
                          )}
                          {call.reason && (
                            <div style={{ marginTop: 4 }}><Text type="warning" style={{ fontSize: '12px' }}>Lý do: {call.reason}</Text></div>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#bfbfbf' }}>
                    Chưa có lịch sử cuộc gọi chăm sóc.
                  </div>
                )}
              </Card>
            )}
          </Col>
        </Row>
      </Form>

      {/* Sticky Save Button Bar at Bottom - Issue #12 */}
      <div style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        padding: '12px 24px',
        marginLeft: -24,
        marginRight: -24,
        marginTop: 8,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
      }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cms/quan-ly-don-hang')}>
          Quay lại
        </Button>
        {!isCreate && (
          <Popconfirm
            title="Xóa đơn hàng này?"
            onConfirm={() => mutateDelete({ id: Number(id) })}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger icon={<DeleteOutlined />}>
              Xóa đơn hàng
            </Button>
          </Popconfirm>
        )}
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={isCreating || isUpdating}
          onClick={handleSubmit}
        >
          Lưu lại
        </Button>
      </div>
    </div>
  );
};

export default DetailDonHang;
